import { useCallback, useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';

import {
  getActionPlan,
  getCriticalPoles,
  saveActionPlan,
} from '../../../../../../api/theHive/redeProto';
import {
  NetworkPole,
  PoleClassification,
  Region,
} from '../../../../../../api/theHive/redeProto/types';
import useErrorMessage from '../../../../../../hooks/useErrorMessage';
import useSnackbar from '../../../../../../hooks/useSnackbar';
import { RedeProtoOutletContext } from '../../types';

interface UseCriticalPolesHook {
  filteredPoles: NetworkPole[];
  ufOptions: string[];
  statusOptions: PoleClassification[];
  region: Region | 'all';
  setRegion: (value: Region | 'all') => void;
  uf: string;
  setUf: (value: string) => void;
  status: PoleClassification | 'all';
  setStatus: (value: PoleClassification | 'all') => void;
  search: string;
  setSearch: (value: string) => void;
  selectedPole: NetworkPole | undefined;
  selectPole: (pole: NetworkPole | undefined) => void;
  actionPlan: string;
  setActionPlan: (value: string) => void;
  savePlan: () => void;
  savingPlan: boolean;
  loading: boolean;
  error: boolean;
}

export default function useCriticalPoles(): UseCriticalPolesHook {
  const [poles, setPoles] = useState<NetworkPole[]>([]);
  const [region, setRegion] = useState<Region | 'all'>('all');
  const [uf, setUf] = useState('all');
  const [status, setStatus] = useState<PoleClassification | 'all'>('all');
  const [search, setSearch] = useState('');
  const [selectedPole, setSelectedPole] = useState<NetworkPole>();
  const [actionPlans, setActionPlans] = useState<Record<number, string>>({});
  const [savingPlan, setSavingPlan] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const { getErrorMessage } = useErrorMessage();
  const { handleSnackbar } = useSnackbar();
  const { selectedNoticeId } = useOutletContext<RedeProtoOutletContext>();

  const getData = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const response = await getCriticalPoles(selectedNoticeId);
      if (response.detail.description) {
        throw new Error(response.detail.description);
      }
      if (!response.data) {
        throw new Error('Algo deu errado, tente novamente.');
      }
      setPoles(response.data);
    } catch (requestError) {
      setError(true);
      handleSnackbar(getErrorMessage(requestError), true);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNoticeId]);

  useEffect(() => {
    getData();
  }, [getData]);

  const ufOptions = useMemo(() => {
    const scoped =
      region === 'all' ? poles : poles.filter((pole) => pole.region === region);
    return Array.from(new Set(scoped.map((pole) => pole.uf))).sort();
  }, [poles, region]);

  const statusOptions = useMemo(
    () =>
      Array.from(
        new Set(
          poles
            .map((pole) => pole.classification)
            .filter((c): c is PoleClassification => c !== null)
        )
      ),
    [poles]
  );

  const filteredPoles = useMemo(() => {
    const term = search.trim().toLowerCase();
    return poles.filter((pole) => {
      if (region !== 'all' && pole.region !== region) return false;
      if (uf !== 'all' && pole.uf !== uf) return false;
      if (status !== 'all' && pole.classification !== status) return false;
      const label = (pole.name ?? pole.city ?? '').toLowerCase();
      if (term && !label.includes(term)) return false;
      return true;
    });
  }, [poles, region, uf, status, search]);

  /** Ao selecionar um polo, busca o plano de ação salvo (uma vez). */
  const selectPole = useCallback(
    (pole: NetworkPole | undefined) => {
      setSelectedPole(pole);
      if (!pole || actionPlans[pole.poleId] !== undefined) return;
      getActionPlan(pole.poleId)
        .then((response) => {
          if (response.data) {
            setActionPlans((prev) => ({
              ...prev,
              [pole.poleId]: response.data?.plan ?? '',
            }));
          }
        })
        .catch(() => {
          /* mantém vazio — plano é opcional */
        });
    },
    [actionPlans]
  );

  const setActionPlan = useCallback(
    (value: string) => {
      if (!selectedPole) return;
      setActionPlans((prev) => ({ ...prev, [selectedPole.poleId]: value }));
    },
    [selectedPole]
  );

  const savePlan = useCallback(async () => {
    if (!selectedPole) return;
    setSavingPlan(true);
    try {
      const plan = actionPlans[selectedPole.poleId] ?? '';
      const response = await saveActionPlan(selectedPole.poleId, plan);
      if (response.detail.description) {
        throw new Error(response.detail.description);
      }
      handleSnackbar('Plano de ação salvo.', false);
    } catch (requestError) {
      handleSnackbar(getErrorMessage(requestError), true);
    } finally {
      setSavingPlan(false);
    }
  }, [selectedPole, actionPlans, handleSnackbar, getErrorMessage]);

  const actionPlan = selectedPole ? actionPlans[selectedPole.poleId] ?? '' : '';

  return {
    filteredPoles,
    ufOptions,
    statusOptions,
    region,
    setRegion,
    uf,
    setUf,
    status,
    setStatus,
    search,
    setSearch,
    selectedPole,
    selectPole,
    actionPlan,
    setActionPlan,
    savePlan,
    savingPlan,
    loading,
    error,
  };
}
